import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaExternalLinkAlt, FaGithub, FaNewspaper, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { projects } from '../content/data';

const ProjectModal = ({ project, onClose, onNavigate }) => {
  const currentIndex = projects.findIndex((p) => p.id === project?.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < projects.length - 1;

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onNavigate(projects[currentIndex - 1]);
      if (e.key === 'ArrowRight' && hasNext) onNavigate(projects[currentIndex + 1]);
    },
    [onClose, onNavigate, currentIndex, hasPrev, hasNext]
  );

  useEffect(() => {
    if (project) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [project, handleKeyDown]);

  if (!project) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="bg-page-light border border-card-border rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-page-light/95 backdrop-blur-sm border-b border-card-border p-4 flex items-center justify-between z-10">
            <div className="flex items-center gap-2">
              <button
                onClick={() => hasPrev && onNavigate(projects[currentIndex - 1])}
                disabled={!hasPrev}
                className="p-2 rounded-lg hover:bg-card disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous project"
              >
                <FaChevronLeft />
              </button>
              <button
                onClick={() => hasNext && onNavigate(projects[currentIndex + 1])}
                disabled={!hasNext}
                className="p-2 rounded-lg hover:bg-card disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="Next project"
              >
                <FaChevronRight />
              </button>
              <span className="text-muted text-caption ml-2">
                {currentIndex + 1} / {projects.length}
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-card transition-colors"
              aria-label="Close modal"
            >
              <FaTimes />
            </button>
          </div>

          <div className="p-6">
            <div className="flex gap-3 mb-4">
              {project.links.demo && (
                <a
                  href={project.links.demo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-sm flex items-center gap-2"
                >
                  <FaExternalLinkAlt className="text-xs" />
                  Live Demo
                </a>
              )}
              {project.links.code && (
                <a
                  href={project.links.code}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-sm flex items-center gap-2"
                >
                  <FaGithub />
                  View Code
                </a>
              )}
              {project.links.caseStudy && (
                <a
                  href={project.links.caseStudy}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-sm flex items-center gap-2"
                >
                  <FaNewspaper />
                  Case Study
                </a>
              )}
            </div>

            <h2 className="text-h1 text-white mb-2">{project.title}</h2>
            <p className="text-muted text-body-lg mb-6">{project.tagline}</p>

            <div className="aspect-video rounded-card overflow-hidden mb-8 border border-card-border">
              <img
                src={`/projects/${project.image}`}
                alt={project.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-h3 text-accent-blue mb-2">Problem</h3>
                <p className="text-muted">{project.problem}</p>
              </div>

              <div>
                <h3 className="text-h3 text-accent-purple mb-2">What I Built</h3>
                <ul className="space-y-2">
                  {project.approach.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted">
                      <span className="text-accent-cyan mt-1">→</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-h3 text-accent-cyan mb-2">Results & Impact</h3>
                <ul className="space-y-2">
                  {project.impact.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted">
                      <span className="text-green-400 mt-1">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-h3 text-white mb-3">Tech Stack</h3>
                <div className="flex flex-wrap gap-2">
                  {project.stack.map((tech) => (
                    <span key={tech} className="chip">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProjectModal;
